import placeholderPic from "assets/img/craft/craft-card-placeholder.svg";
import { useContext, useLayoutEffect, useState } from "react";
import { CraftPredictionContext } from "pages/CraftPrediction";
import CraftingPredictionPopup from "./CraftingPredictionPopup";
import { createAPI } from "services/api/api";
import CommonItemInfo from "components/common-item/CommonItemInfo";
import { useDispatch } from "react-redux";
import { clearCraftBlock } from "store/crafting";
import { clearPredictionBlock } from "utils/crafting";
import { getEligibles } from "services/craftPredictionService";
import CraftBlockRarity from "components/crafting/CraftBlockRarity";
import { useSoundPath } from "hooks/useSoundPath";
import CraftBlockItemTooltip from "components/crafting/CraftBlockItemTooltip";
import CraftBlockPredictionCombine from "./CraftBlockPredictionCombine";
import CraftBlockIdentityTooltip from "components/craft-identity/CraftBlockIdentityTooltip";

const CraftBlockPrediction = () => {
  const api = createAPI();
  const dispatch = useDispatch();
  const [isCraftLoading, setIsCraftLoading] = useState(false);
  const [isCraft, setIsCraft] = useState(false);
  const [craftingStep, setCraftingStep] = useState("submit");
  const [craftedPrediction, setCraftedPrediction] = useState({});
  const [errorText, setErrorText] = useState("");
  const playSound = useSoundPath();
  const {
    activeBlock,
    setActiveBlock,
    clearBlocks,
    craftBlocks,
    setIsEligiblesLoading,
    setEligibles,
  } = useContext(CraftPredictionContext);

  const clearBlock = (block) => {
    clearPredictionBlock(dispatch, clearCraftBlock, block, setEligibles);
    playSound("clearing");
  };

  const isDisabledCheck = (block) => {
    if (!craftBlocks[0].value || !craftBlocks[1].value) {
      if (block.placeholder === "Trigger" || block.placeholder === "Prize") {
        return "disabled";
      } else {
        return "";
      }
    } else if (
      ((craftBlocks[0].value !== null || craftBlocks[1].value !== null) &&
        craftBlocks[2].value !== null) ||
      ((craftBlocks[0].value !== null || craftBlocks[1].value !== null) &&
        craftBlocks[3].value !== null)
    ) {
      if (block.placeholder === "Identity" || block.placeholder === "Event") {
        return "disabled";
      }
    }
  };

  const getClearBtn = () => {
    if (
      craftBlocks.length > 0 &&
      craftBlocks.some((obj) => obj.value !== null)
    ) {
      return (
        <button
          className="craft-block__clear"
          onClick={() => {
            clearBlocks();
            setActiveBlock("Identity");
            playSound("clearing");
          }}
        >
          Clear all slots
        </button>
      );
    } else return null;
  };

  const isSubmitDisabled = () => {
    if (
      craftBlocks.length > 0 &&
      craftBlocks.every((item) => item.value !== null)
    ) {
      return false;
    } else return true;
  };

  const submitHandler = (e) => {
    e.preventDefault();

    setIsCraftLoading(true);
    playSound("crafting");

    const payload = {
      nft_card_identity_id: craftBlocks[0].value.id,
      nft_card_event_date_id: craftBlocks[1].value.id,
      nft_card_trigger_id: craftBlocks[2].value.id,
      nft_card_prize_id: craftBlocks[3].value.id,
    };

    api
      .post("/nft/craft_prediction", payload)
      .then((res) => {
        if (res.status === 200) {
          setIsCraftLoading(false);
          setCraftingStep("success");
          setCraftedPrediction(res.data);
        }
      })
      .catch((error) => {
        console.log("prediction craft error", error);
        if (error.response.data.includes("Event time has already passed")) {
          setCraftingStep("rejected");
        } else {
          setCraftingStep("fail");
          setErrorText(error.response.data);
        }
        setIsCraftLoading(false);
      });
  };

  useLayoutEffect(() => {
    if (craftBlocks.some((item) => item.value !== null)) {
      getEligibles(
        craftBlocks,
        activeBlock,
        setIsEligiblesLoading,
        setEligibles
      );
    }
  }, [craftBlocks, activeBlock]);

  return (
    <>
      <section className="craft-block">
        <header className="craft-block__header">
          <section className="craft-block__header-info">
            <h1 className="craft-block__title">
              Craft prediction
              <CraftBlockIdentityTooltip />
            </h1>
            <p className="craft-block__descr">
              Select an Event or Identity to start, add the remaining cards, and
              the prize you want to play for.
            </p>
          </section>
          <div className="craft-block__actions">
            {getClearBtn()}
            <button
              className={`craft-block__submit ${isSubmitDisabled() ? '' : 'craft-block__submit--enabled'}`}
              disabled={isSubmitDisabled()}
              onClick={() => setIsCraft(true)}
            >
              Craft Prediction
            </button>
          </div>
        </header>
        <ul className={`craft-block__steps ${craftBlocks.every((item) => item.value !== null) ? 'craft-block__steps--filled' : ''}`}>
          {craftBlocks.map((block) => (
            <li
              className={`craft-block__step
                ${block.placeholder === activeBlock ? "active" : ""}
                ${isDisabledCheck(block)}
              `}
              key={block.placeholder}
              onClick={() => setActiveBlock(block.placeholder)}
            >
              {block.value === null ? (
                <p className="craft-block__step-empty">
                  <img src={placeholderPic} alt="" />
                  <span>
                    {block.placeholder}
                    {activeBlock === "Prize" &&
                      block.placeholder === "Prize" && (
                        <CraftBlockPredictionCombine />
                      )}
                  </span>
                </p>
              ) : (
                <>
                  <CraftBlockItemTooltip item={block.value} />
                  <div className="craft-block__step-selected">
                    <CommonItemInfo
                      image={block.value.image}
                      name={block.value.name}
                      props={block.value.properties}
                      type={block.value.tag}
                    />
                    <p className="cards-list__block-item-text">
                      {block.value.tag.toLowerCase() === "prize"
                        ? "$" + block.value.name
                        : block.value.name}
                    </p>
                    <span className="craft-block__step-tag">
                      {block.value.tag}
                    </span>
                    <button
                      className="craft-block__step-clear"
                      onClick={() => {
                        clearBlock(block);
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
        <CraftBlockRarity />
      </section>

      {isCraft && (
        <CraftingPredictionPopup
          setIsCraft={setIsCraft}
          submitHandler={submitHandler}
          craftingStep={craftingStep}
          setCraftingStep={setCraftingStep}
          isCraftLoading={isCraftLoading}
          errorText={errorText}
        />
      )}
    </>
  );
};

export default CraftBlockPrediction;
